import { Checkbox, FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, Select } from '@mui/material';
import { filter, includes, isEmpty, map, size } from 'es-toolkit/compat';
import moment from 'moment';
import React from 'react';

import { Orchestration } from 'api/experimentResults';
import Loader from 'components/Loader';
import { ExportScopeField, ExportScopeValue } from 'components/ResultsExport/ExportFormFields';
import { ExportErrorAlert } from 'components/ResultsExport/ExportErrorAlert';
import { Study } from 'interfaces/study';
import { buildExportErrorReport, describeExportFailure } from 'services/export/exportErrorReport';
import { useCurrentLabId } from 'utils/useCurrentLab';
import {
  getWorkflowSourceAvailability,
  WorkflowExportKind,
  WorkflowResultsSource,
  WorkflowResultsSourceSelection,
  workflowResultsCheckingMessage,
  workflowRunsFailedNote,
} from 'services/export/workflowExportSource';

interface WorkflowResultsSourceSelectProps {
  kind: WorkflowExportKind;
  study: Study;
  selection: WorkflowResultsSourceSelection;
  onChange: (selection: WorkflowResultsSourceSelection) => void;
  runs: Orchestration[];
  // Run ids in the dialog link that are not runs of this study; they are ignored, and the user is told.
  staleRunIds?: string[];
  isLoadingRuns: boolean;
  hasRunsError?: boolean;
  // Job name of each run, by run id. A run without one is listed as an unnamed run, never by its id.
  runNames?: Record<string, string | undefined>;
  // Why the runs could not be loaded; shown with its HTTP status and support details.
  runsError?: unknown;
}

const sourceOptions: { source: WorkflowResultsSource; label: string }[] = [
  { source: 'published', label: 'Published results' },
  { source: 'approved', label: 'Approved results (not yet published)' },
  { source: 'run', label: 'A specific post-processing run' },
];

const SELECT_ALL_RUNS = '__select-all-runs__';
const CLEAR_RUNS = '__clear-runs__';

const UNNAMED_RUN = 'Unnamed run';

const getRunName = (runId: string, runNames: Record<string, string | undefined>) =>
  runNames[runId]?.trim() || UNNAMED_RUN;

// `<job name>, created <date>`; runs that would read the same get the time as well.
const getRunLabels = (runs: Orchestration[], runNames: Record<string, string | undefined>) => {
  const label = (run: Orchestration, format: string) =>
    `${getRunName(run.orchestrationId, runNames)}${
      run.createdAtMinimum ? `, created ${moment(run.createdAtMinimum).format(format)}` : ''
    }`;
  const dayLabels = map(runs, (run) => label(run, 'LL'));
  return Object.fromEntries(
    map(runs, (run, index) => [
      run.orchestrationId,
      filter(dayLabels, (dayLabel) => dayLabel === dayLabels[index]).length > 1 ? label(run, 'LLL') : dayLabels[index],
    ])
  ) as Record<string, string>;
};

const describeStaleRuns = (count: number) =>
  count === 1
    ? '1 run in the link is not available for this export and was ignored.'
    : `${count} runs in the link are not available for this export and were ignored.`;

const describeRunSelection = (runIds: string[], runNames: Record<string, string | undefined>) =>
  size(runIds) === 0
    ? 'No runs selected'
    : size(runIds) === 1
    ? getRunName(runIds[0], runNames)
    : `${size(runIds)} runs selected`;

// Only rendered for users who may view unpublished results. Until the runs are known no option exists to choose,
// so a source is never picked from a partial picture. Any number of runs may be chosen; each is exported on its own.
export const WorkflowResultsSourceSelect: React.FC<WorkflowResultsSourceSelectProps> = ({
  kind,
  study,
  selection,
  onChange,
  runs,
  staleRunIds = [],
  isLoadingRuns,
  hasRunsError = false,
  runNames = {},
  runsError,
}) => {
  const { labId } = useCurrentLabId();
  if (isLoadingRuns) {
    return (
      <ExportScopeValue label="Results">
        <Loader inline message={workflowResultsCheckingMessage} />
      </ExportScopeValue>
    );
  }

  const availabilityOf = (source: WorkflowResultsSource) =>
    getWorkflowSourceAvailability({
      kind,
      study,
      selection: { source },
      runCount: runs.length,
      canViewUnpublishedResults: true,
    });
  const runAvailability = availabilityOf('run');
  const runsFailure = describeExportFailure(runsError, workflowRunsFailedNote);
  const allRunIds = map(runs, 'orchestrationId');
  const selectedRunIds = selection.runIds ?? [];
  const runLabels = getRunLabels(runs, runNames);
  // Without published or approved results there is nothing to choose between, so only the runs are offered.
  const runsOnly = availabilityOf('published').disabled && availabilityOf('approved').disabled;

  const runsErrorAlert = hasRunsError ? (
    <ExportErrorAlert
      title="The post-processing runs could not be loaded"
      errors={[runsFailure]}
      report={buildExportErrorReport({
        operation: kind === 'features' ? 'Export features (list runs)' : 'Export cell tables (list runs)',
        studyId: study.id,
        labId,
        source: 'Post-processing run list',
        errors: [runsFailure],
      })}
    />
  ) : null;

  const runPicker =
    (runsOnly || selection.source === 'run') && !runAvailability.disabled ? (
      <FormControl size="small" fullWidth sx={{ mt: runsOnly ? 0 : 1 }}>
        <InputLabel id="workflow-export-run-label" shrink>
          Post-processing runs
        </InputLabel>
        <Select
          multiple
          displayEmpty
          notched
          labelId="workflow-export-run-label"
          label="Post-processing runs"
          value={selectedRunIds}
          renderValue={(runIds) => describeRunSelection(runIds, runNames)}
          onChange={(event) => {
            const value = event.target.value;
            const chosen = typeof value === 'string' ? value.split(',') : value;
            if (includes(chosen, SELECT_ALL_RUNS)) return onChange({ source: 'run', runIds: allRunIds });
            if (includes(chosen, CLEAR_RUNS)) return onChange({ source: 'run', runIds: [] });
            // Keep the runs in the order they are listed, whatever order they were picked in.
            return onChange({ source: 'run', runIds: filter(allRunIds, (runId) => includes(chosen, runId)) });
          }}
        >
          <MenuItem value={SELECT_ALL_RUNS} divider>
            Select all runs
          </MenuItem>
          <MenuItem value={CLEAR_RUNS} divider disabled={size(selectedRunIds) === 0}>
            Clear
          </MenuItem>
          {map(runs, (run) => (
            <MenuItem key={run.orchestrationId} value={run.orchestrationId}>
              <Checkbox
                edge="start"
                size="small"
                checked={includes(selectedRunIds, run.orchestrationId)}
                tabIndex={-1}
              />
              <ListItemText primary={runLabels[run.orchestrationId]} />
            </MenuItem>
          ))}
        </Select>
        {!isEmpty(staleRunIds) && <FormHelperText>{describeStaleRuns(size(staleRunIds))}</FormHelperText>}
      </FormControl>
    ) : null;

  if (runsOnly) {
    return (
      <ExportScopeValue label="Results">
        {runsErrorAlert}
        {runPicker}
        {!runPicker && !runsErrorAlert && <span>{runAvailability.note}</span>}
      </ExportScopeValue>
    );
  }

  return (
    <ExportScopeField
      id="workflow-export-results"
      label="Results"
      options={map(sourceOptions, ({ source, label }) => {
        const { disabled, note } = availabilityOf(source);
        return {
          value: source,
          label,
          disabled,
          note: disabled ? (source === 'run' && hasRunsError ? workflowRunsFailedNote : note) : undefined,
        };
      })}
      value={selection.source}
      onChange={(source) => onChange({ source: source as WorkflowResultsSource })}
    >
      {runsErrorAlert}
      {runPicker}
    </ExportScopeField>
  );
};
